import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ArtistCard from "../ArtistCard.js";
import { API_BASE } from "../../api.js";
import { supabase } from "../../lib/supabase.js";
import "./ArtistsList.css";

// Normalise Supabase or MongoDB artist to a common shape
const norm = (a, fromSupa) => ({
  id:           fromSupa ? a.id   : a._id,
  name:         a.name          || "",
  name_ar:      a.name_ar       || "",
  specialty:    a.specialty     || "",
  specialty_ar: a.specialty_ar  || "",
  country:      a.country       || "",
  intro:        a.intro         || a.biography || "",
  intro_ar:     a.intro_ar      || "",
  photo_url:    a.photo_url     || a.profileImage || "",
  images:       Array.isArray(a.images) ? a.images : [],
  is_elder:     fromSupa ? (a.is_elder ?? false) : null,
  fromSupa,
});

export default function ArtistsList() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar" || i18n.language?.startsWith("ar");

  const [artists, setArtists]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState(null);
  const [query,   setQuery]     = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase
          .from("artists")
          .select("*")
          .eq("status", "approved")
          .eq("is_visible", true)
          .order("sort_order", { nullsFirst: false })
          .order("name");

        if (data && data.length > 0) {
          setArtists(data.map((a) => norm(a, true)));
        } else {
          const res = await axios.get(`${API_BASE}/getRally`);
          setArtists((res.data || []).map((a) => norm(a, false)));
        }
      } catch {
        setError(t("artists.error"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return artists;
    return artists.filter((a) =>
      [a.name, a.name_ar, a.specialty, a.specialty_ar, a.country, a.intro, a.intro_ar]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [artists, query]);

  const { elders, news } = useMemo(() => {
    const allFromSupa = filtered.every((a) => a.fromSupa);
    if (allFromSupa) {
      return {
        elders: filtered.filter((a) =>  a.is_elder),
        news:   filtered.filter((a) => !a.is_elder),
      };
    }
    const sorted = [...filtered].sort((a, b) =>
      String(b.id).localeCompare(String(a.id))
    );
    const mid = Math.ceil(sorted.length * 0.5);
    return { elders: sorted.slice(mid).reverse(), news: sorted.slice(0, mid) };
  }, [filtered]);

  if (loading) return (
    <div className="load-state">
      <div className="load-state-spinner" />
      {t("artists.loading")}
    </div>
  );
  if (error) return <div className="load-state">{error}</div>;

  return (
    <div className="artistslist-page spar-page">
      <div className="al-search-wrap">
        <input
          className="al-search-input"
          type="text"
          placeholder={t("search.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      {elders.length > 0 && (
        <>
          <h2 className="spar-section-title">{t("artists.elders")}</h2>
          <div className="art-grid" style={{ marginBottom: 52 }}>
            {elders.map((a) => (
              <ArtistCard key={a.id} artist={a} variant="elder" isAr={isAr} />
            ))}
          </div>
        </>
      )}

      <h2 className="spar-section-title">{t("artists.new")}</h2>
      <div className="art-grid">
        {news.map((a) => (
          <ArtistCard key={a.id} artist={a} variant="new" isAr={isAr} />
        ))}
      </div>

      {elders.length === 0 && news.length === 0 && (
        <div className="load-state">{t("artists.empty") || "No artists yet."}</div>
      )}
    </div>
  );
}
